import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Phone, Calendar, User, Share2, Facebook, Linkedin, LinkIcon, Heart, Gift, Coins, Trash2, Quote } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import OptimizedImage from '../../components/OptimizedImage';
import SEO from '../../components/SEO';
import { toast } from 'sonner';

const SLUG = 'vider-maison-parents-conseils';
const PUBLISH_DATE = '2026-08-08';

const BlogViderMaisonParents = () => {
  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Vider la maison de ses parents : conseils pratiques et émotionnels",
    "description": "Comment vider la maison de ses parents sans s'épuiser : préparation mentale, méthode des 4 boîtes, gestion des désaccords familiaux et moment où déléguer à un professionnel.",
    "image": `https://debeco.fr/blog/${SLUG}/hero.jpg`,
    "datePublished": PUBLISH_DATE,
    "dateModified": PUBLISH_DATE,
    "author": { "@type": "Organization", "name": "DEBECO" },
    "publisher": {
      "@type": "Organization",
      "name": "DEBECO",
      "logo": { "@type": "ImageObject", "url": "https://debeco.fr/images/logo-header.png" }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://debeco.fr/blog/${SLUG}` }
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://debeco.fr/blog" },
      { "@type": "ListItem", "position": 3, "name": "Vider la maison de ses parents", "item": `https://debeco.fr/blog/${SLUG}` }
    ]
  };

  useEffect(() => {
    const scripts = [jsonLdArticle, jsonLdBreadcrumb].map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-managed', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => s.remove());
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(`https://debeco.fr/blog/${SLUG}`);
    toast.success('Lien copié !');
  };

  const boites = [
    {
      icon: Heart,
      color: 'from-emerald-600 to-emerald-500',
      border: 'border-emerald-200',
      bg: 'bg-emerald-50',
      titre: 'Boîte 1 — Garder',
      regle: 'Ce qui a une vraie place dans votre vie, pas dans un carton au garage.',
      desc: 'Albums photos, correspondances, bijoux de famille, un ou deux meubles auxquels vous tenez vraiment. Fixez-vous une limite physique : ce qui tient dans votre voiture ou dans une pièce de votre logement. Au-delà, vous déplacez le problème au lieu de le régler.'
    },
    {
      icon: Gift,
      color: 'from-blue-700 to-blue-600',
      border: 'border-blue-200',
      bg: 'bg-blue-50',
      titre: 'Boîte 2 — Donner',
      regle: 'Ce qui peut encore servir, mais pas à vous.',
      desc: 'Vaisselle, linge de maison, livres, petit électroménager fonctionnel. Autour d’Annecy, Emmaüs et les ressourceries du bassin acceptent la plupart des dons en bon état. Donner allège la culpabilité de « jeter » : les affaires de vos parents continuent leur vie ailleurs.'
    },
    {
      icon: Coins,
      color: 'from-amber-600 to-amber-500',
      border: 'border-amber-200',
      bg: 'bg-amber-50',
      titre: 'Boîte 3 — Vendre',
      regle: 'Ce qui a une valeur marchande réelle — soyez lucide.',
      desc: 'Meubles anciens de qualité, montres, argenterie, outillage, collections. Faites estimer les belles pièces par un brocanteur ou un commissaire-priseur avant de fixer un prix. Attention : le mobilier « rustique » des années 70-80 se vend mal, ne surestimez pas cette boîte.'
    },
    {
      icon: Trash2,
      color: 'from-gray-600 to-gray-500',
      border: 'border-gray-200',
      bg: 'bg-gray-50',
      titre: 'Boîte 4 — Jeter (recycler)',
      regle: 'Ce qui est usé, cassé ou sans preneur.',
      desc: 'Matelas, mousse, aggloméré abîmé, textiles usés, papiers sans valeur. Direction les déchetteries du SILA autour du lac — ou la benne du professionnel si vous déléguez. Triez par matière quand c’est possible : bois, métal et carton se recyclent très bien.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <SEO
        title="Vider la maison de ses parents : 5 conseils clés | DEBECO"
        description="Vider la maison de ses parents sans s'épuiser : méthode des 4 boîtes, gestion de la fratrie, souvenirs à préserver. Les conseils terrain d'une équipe d'Annecy."
        canonical={`/blog/${SLUG}`}
        ogImage={`/blog/${SLUG}/hero.jpg`}
        ogType="article"
      />

      {/* Breadcrumb */}
      <nav className="bg-white border-b" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <ol className="flex items-center text-sm text-gray-600 flex-wrap gap-y-1">
            <li><Link to="/" className="hover:text-emerald-600">Accueil</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/blog" className="hover:text-emerald-600">Blog</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li className="text-blue-900 font-medium">Vider la maison de ses parents</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">Conseils & Émotions</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-6" data-testid="article-h1">Vider la maison de ses parents : comment traverser cette étape sans s’épuiser</h1>
            <div className="flex flex-wrap items-center gap-6 text-blue-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <time dateTime={PUBLISH_DATE}>8 août 2026</time>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>DEBECO</span>
              </div>
              <span className="text-blue-300">~9 min de lecture</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Hero image */}
            <figure className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
              <OptimizedImage
                src={`/blog/${SLUG}/hero.jpg`}
                webpSrc={`/blog/${SLUG}/hero.webp`}
                alt="Vider la maison de ses parents conseils"
                className="w-full h-96 object-cover"
                width="1264"
                height="848"
                decoding="async"
                data-testid="article-hero-image"
              />
              <figcaption className="bg-gray-100 px-4 py-2 text-xs text-gray-500 text-center">
                Derrière chaque objet, un souvenir — et une décision à prendre. Avancer par étapes rend la tâche possible.
              </figcaption>
            </figure>

            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <div className="prose prose-lg max-w-none text-gray-700">

                  {/* Introduction */}
                  <p className="text-xl leading-relaxed mb-6">
                    Un jour, on pousse la porte de la maison de ses parents avec un trousseau de clés qui ne devrait pas être dans notre poche. Que ce soit après un décès, un départ en maison de retraite ou une vente devenue nécessaire, il faut désormais décider du sort de chaque assiette, chaque manteau, chaque tiroir. Et personne ne nous a appris à faire ça.
                  </p>
                  <p className="text-lg leading-relaxed mb-6">
                    En dix ans d’interventions dans le bassin annécien — des appartements de Novel aux fermes rénovées au-dessus de Talloires — nous avons accompagné des dizaines de familles dans cette situation. Cet article rassemble ce que nous avons appris à leurs côtés : comment se préparer, quelle méthode de tri adopter, comment éviter que la fratrie ne se déchire, et à quel moment passer le relais. Pas de recette miracle, mais des repères concrets qui fonctionnent.
                  </p>

                  {/* H2 - Pourquoi c'est si difficile */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Pourquoi c’est si difficile (et pourquoi c’est normal)</h2>
                  <p className="mb-4">
                    On sous-estime toujours cette tâche, pour deux raisons qui se cumulent.
                  </p>
                  <p className="mb-4">
                    <strong>La charge matérielle, d’abord.</strong> Une maison habitée quarante ans par la même famille contient en moyenne 30 à 60 m³ d’affaires — l’équivalent de trois à six camions de déménagement. Les pavillons du bassin annécien, avec leur cave, leur grenier et leur garage double, se situent souvent dans le haut de cette fourchette. À raison de week-ends espacés, une famille seule met couramment trois à six mois pour en venir à bout.
                  </p>
                  <p className="mb-6">
                    <strong>La charge émotionnelle, surtout.</strong> Trier les affaires de ses parents, c’est feuilleter leur vie sans eux. L’agenda de 1987 retrouvé dans le secrétaire, l’odeur d’un gilet, les dessins d’enfant conservés dans une boîte à chaussures : chaque découverte interrompt le travail et rouvre quelque chose. Beaucoup de nos clients décrivent aussi une forme d’inversion des rôles difficile à vivre — décider à la place de ceux qui ont toujours décidé pour nous. Si vous vous sentez dépassé, ce n’est pas un manque d’organisation : c’est la nature même de la tâche.
                  </p>

                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 my-6 rounded-r-lg">
                    <p className="text-amber-900 font-medium mb-1">À savoir si une succession est en cours</p>
                    <p className="text-amber-800 text-sm">
                      Tant que l’acte de notoriété n’est pas établi par le notaire, ne jetez rien et ne vendez rien : les biens appartiennent à l’indivision. Vous pouvez en revanche commencer à trier et à inventorier. Les aspects juridiques (délais, notaire, inventaire) sont détaillés dans notre <Link to="/blog/debarras-succession-annecy-guide" className="text-amber-800 font-medium underline hover:text-amber-900">guide du débarras succession à Annecy pour les héritiers</Link>.
                    </p>
                  </div>

                  {/* H2 - Se préparer */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Se préparer avant d’ouvrir la première armoire</h2>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">Choisir le bon moment — pas le plus tôt possible</h3>
                  <p className="mb-6">
                    Sauf contrainte de bail ou de vente imminente, rien n’oblige à commencer dans les semaines qui suivent un décès ou un départ en EHPAD. Les familles qui s’y mettent trop tôt prennent des décisions qu’elles regrettent — tout garder par peur de mal faire, ou tout jeter pour en finir vite. Un délai de quelques semaines à deux mois permet d’aborder le tri avec plus de recul. À l’inverse, ne laissez pas la maison fermée plus d’un an : l’humidité abîme le mobilier, et la tâche repoussée devient une montagne.
                  </p>

                  <h3 className="text-xl font-bold text-blue-900 mt-8 mb-4">Réunir la fratrie et fixer les règles avant de toucher au premier objet</h3>
                  <p className="mb-4">
                    La plupart des conflits que nous observons ne naissent pas de la valeur des objets, mais de l’absence de règles posées au départ. Avant la première session de tri, organisez une réunion — sur place idéalement — et mettez-vous d’accord sur trois points :
                  </p>
                  <ul className="mb-6 space-y-2 list-disc pl-6">
                    <li><strong>Qui participe et qui décide.</strong> Un frère installé à l’étranger peut donner procuration ou choisir sur photos, mais cela doit être dit explicitement.</li>
                    <li><strong>Le calendrier.</strong> Combien de week-ends, avec quelle échéance finale ? Une date de fin, même souple, évite l’enlisement.</li>
                    <li><strong>La méthode de répartition des objets convoités.</strong> Tour à tour, chacun choisit un objet — comme une draft sportive. Simple, équitable, et cela désamorce 90 % des tensions.</li>
                  </ul>

                  {/* H2 - Méthode des 4 boîtes */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">La méthode des 4 boîtes : garder, donner, vendre, jeter</h2>
                  <p className="mb-6">
                    C’est la méthode que nous recommandons à toutes les familles, parce qu’elle transforme une question paralysante (« qu’est-ce qu’on fait de tout ça ? ») en une suite de micro-décisions binaires. Le principe : chaque objet manipulé rejoint immédiatement l’une des quatre destinations. On ne repose jamais un objet là où on l’a pris.
                  </p>

                  {/* Schéma 4 boîtes */}
                  <div className="my-8" data-testid="methode-4-boites">
                    <div className="rounded-t-xl bg-blue-950 px-6 py-4">
                      <h3 className="text-white font-bold text-lg m-0">La méthode des 4 boîtes — Mode d’emploi</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 border-x border-b rounded-b-xl overflow-hidden">
                      {boites.map((b) => (
                        <div key={b.titre} className={`${b.bg} border ${b.border} p-5 m-2 rounded-xl`}>
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`w-10 h-10 bg-gradient-to-br ${b.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <b.icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="font-bold text-blue-900 m-0">{b.titre}</p>
                              <p className="text-xs text-gray-500 m-0 italic">{b.regle}</p>
                            </div>
                          </div>
                          <p className="text-gray-600 text-sm m-0 leading-relaxed">{b.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 my-6 rounded-r-lg">
                    <p className="text-emerald-900 font-medium mb-1">L’astuce qui change tout</p>
                    <p className="text-emerald-800 text-sm">
                      Autorisez-vous une cinquième boîte « Je ne sais pas encore » — mais donnez-lui une taille maximale (un seul carton) et une date de péremption (un mois). À l’échéance, ce qui n’a pas trouvé de place dans votre vie rejoint la boîte « Donner ». Sans cette limite, la boîte de l’indécision engloutit toute la maison.
                    </p>
                  </div>

                  <p className="mb-6">
                    Commencez toujours par les pièces à faible charge affective — garage, buanderie, cave — et gardez les chambres et le bureau pour la fin, quand la mécanique de décision est rodée. Et travaillez par sessions courtes : au-delà de trois heures, la fatigue émotionnelle fait basculer les objets au hasard d’une boîte à l’autre.
                  </p>

                  {/* H2 - Désaccords familiaux */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Gérer les désaccords familiaux sans y laisser des liens</h2>
                  <p className="mb-4">
                    Le service à café que deux sœurs veulent, le piano que personne ne veut mais que personne n’ose jeter, le frère absent qui conteste chaque décision par téléphone : les points de friction sont prévisibles. Quelques garde-fous éprouvés :
                  </p>
                  <ul className="mb-4 space-y-2 list-disc pl-6">
                    <li><strong>Photographiez tout et partagez l’inventaire.</strong> Un album partagé (une heure de travail avec un téléphone) permet aux absents de choisir à distance et coupe court aux soupçons de « servez-vous en premier ».</li>
                    <li><strong>Pour les objets de valeur, faites appel à un tiers neutre.</strong> Une estimation par un commissaire-priseur ou un antiquaire — il en existe plusieurs entre Annecy et le tour du lac — remplace les impressions par un chiffre. On se dispute rarement sur un montant écrit.</li>
                    <li><strong>Ne tranchez jamais un désaccord à chaud.</strong> Mettez l’objet litigieux de côté, dans une pièce dédiée, et revenez-y à la session suivante. La plupart des conflits se dégonflent en une semaine.</li>
                    <li><strong>Rappelez-vous l’enjeu réel.</strong> Dans dix ans, personne ne se souviendra de qui a eu la soupière. Tout le monde se souviendra de la manière dont la famille s’est parlé pendant ces semaines-là.</li>
                  </ul>

                  {/* Témoignage */}
                  <div className="my-8 rounded-xl border-2 border-blue-200 bg-blue-50 p-6" data-testid="temoignage-client">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                        <Quote className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-blue-900 italic leading-relaxed mb-3 m-0">
                          « Avec mon frère, on tournait en rond depuis des mois dans la maison de nos parents à Sevrier. On n’arrivait pas à jeter, on n’arrivait pas à décider. Le déclic, ça a été la visite de l’équipe : ils nous ont dit quoi mettre de côté, et ils se sont occupés de tout le reste en deux jours. J’aurais dû appeler six mois plus tôt. »
                        </p>
                        <p className="text-sm text-gray-500 m-0">— Catherine M., Sevrier (témoignage publié avec son accord)</p>
                      </div>
                    </div>
                  </div>

                  {/* H2 - Quand déléguer */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Quand faire appel à un professionnel ?</h2>
                  <p className="mb-4">
                    Déléguer n’est pas abandonner. La règle que nous donnons aux familles : <strong>gardez pour vous le tri des souvenirs, confiez le reste.</strong> Personne d’autre que vous ne peut décider du sort des photos ou des lettres. En revanche, démonter une armoire normande, descendre un congélateur d’un troisième étage sans ascenseur près de la gare d’Annecy, ou écouler quinze m³ de mobilier invendable — cela, un professionnel le fait mieux, plus vite et sans risque pour votre dos ni vos week-ends.
                  </p>
                  <p className="mb-4">
                    Certains signaux indiquent qu’il est temps de passer le relais : vous habitez loin et chaque aller-retour coûte un week-end ; la vente du bien est engagée et le compromis fixe une échéance ; le volume dépasse ce qu’une voiture et quelques trajets en déchetterie peuvent absorber ; ou, tout simplement, vous n’avancez plus depuis des semaines et la maison pèse sur toute la famille.
                  </p>
                  <p className="mb-6">
                    Concrètement, une fois vos boîtes « Garder » à l’abri, une équipe spécialisée vide l’intégralité du bien en un à trois jours : c’est exactement le périmètre de notre <Link to="/debarras-succession-annecy" className="text-emerald-600 hover:text-emerald-700 font-medium">service de débarras succession à Annecy</Link> lorsque le contexte est un héritage, ou de notre <Link to="/vide-maison-annecy" className="text-emerald-600 hover:text-emerald-700 font-medium">prestation de vide-maison intégral à Annecy</Link> lorsque la maison doit être rendue vide pour une vente ou un départ en résidence. Dans les deux cas, ce qui peut resservir part vers les associations et ressourceries locales plutôt qu’à la benne — une manière de prolonger, un peu, ce que vos parents ont construit. Cette conviction est au cœur de <Link to="/a-propos" className="text-emerald-600 hover:text-emerald-700 font-medium">l’histoire et des valeurs de DEBECO</Link>.
                  </p>

                  <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6 rounded-r-lg">
                    <p className="text-red-900 font-medium mb-1">Erreur fréquente</p>
                    <p className="text-red-800 text-sm">
                      Vider soi-même « pour économiser », puis louer une benne. Entre la location (300 à 500 € la semaine), les trajets en déchetterie, les congés posés et le risque de jeter un objet de valeur non identifié, le bilan est rarement gagnant face à un devis professionnel ferme — surtout quand la valorisation du mobilier vient le réduire.
                    </p>
                  </div>

                  {/* Conclusion */}
                  <h2 className="text-2xl font-bold text-blue-900 mt-12 mb-6">Avancer à votre rythme — mais avancer</h2>
                  <p className="mb-4">
                    Vider la maison de ses parents n’est pas une corvée logistique : c’est la dernière étape d’une histoire familiale, et elle mérite d’être faite correctement — ni bâclée, ni éternisée. Fixez un cadre avec la fratrie, appliquez la méthode des 4 boîtes, protégez les souvenirs, et n’hésitez pas à confier la partie lourde à des mains expérimentées quand elle dépasse les vôtres.
                  </p>
                  <p className="mb-6">
                    Si vous êtes dans cette situation quelque part entre Annecy, le tour du lac et les vallées voisines, nous pouvons passer voir la maison, sans engagement. Une visite d’une heure suffit souvent à transformer une montagne en un simple planning.
                  </p>

                </div>

                {/* Pour aller plus loin */}
                <div className="mt-12 border-t-2 pt-10">
                  <h2 className="text-2xl font-bold text-blue-900 mb-6">Pour aller plus loin</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Link to="/blog/debarras-succession-annecy-guide" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all h-full">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Succession à Annecy : le guide des héritiers</h3>
                        <p className="text-gray-600 text-sm">Délais légaux, rôle du notaire, inventaire et devenir des objets, étape par étape.</p>
                      </div>
                    </Link>
                    <Link to="/debarras-succession-annecy" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all h-full">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Débarras succession à Annecy</h3>
                        <p className="text-gray-600 text-sm">Notre accompagnement dédié aux familles : tri respectueux, coordination notaire.</p>
                      </div>
                    </Link>
                    <Link to="/vide-maison-annecy" className="group">
                      <div className="p-5 bg-blue-50 rounded-xl border-2 border-transparent hover:border-emerald-500 transition-all h-full">
                        <h3 className="font-bold text-blue-900 mb-2 group-hover:text-emerald-700 transition-colors">Vide-maison à Annecy</h3>
                        <p className="text-gray-600 text-sm">La maison rendue entièrement vide, à date fixe, avant vente ou remise des clés.</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Share */}
                <div className="mt-10 pt-6 border-t flex items-center gap-4 flex-wrap">
                  <span className="text-gray-500 text-sm font-medium flex items-center gap-2"><Share2 className="w-4 h-4" /> Partager :</span>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=https://debeco.fr/blog/${SLUG}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors" aria-label="Partager sur Facebook">
                    <Facebook className="w-5 h-5 text-blue-700" />
                  </a>
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=https://debeco.fr/blog/${SLUG}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors" aria-label="Partager sur LinkedIn">
                    <Linkedin className="w-5 h-5 text-blue-700" />
                  </a>
                  <button onClick={copyLink} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm text-gray-600" aria-label="Copier le lien">
                    <LinkIcon className="w-5 h-5" /> Copier le lien
                  </button>
                </div>

                {/* CTA */}
                <div className="mt-10 p-8 bg-gradient-to-br from-blue-900 to-emerald-800 rounded-2xl text-white text-center">
                  <h3 className="text-2xl font-bold mb-3">Besoin d’un débarras à Annecy ?</h3>
                  <p className="text-blue-100 mb-6">Devis gratuit en 24h. Visite sans engagement, accompagnement humain, valorisation et recyclage local.</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/contact">
                      <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl w-full sm:w-auto" data-testid="cta-devis">
                        Demander un devis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </Link>
                    <a href="tel:0451401617">
                      <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 w-full sm:w-auto">
                        <Phone className="mr-2 w-5 h-5" />
                        04 51 40 16 17
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogViderMaisonParents;
